// src/components/prims.tsx — heritage primitives ported from the prototype
// bp-ui.jsx (Eyebrow, Card, Pill, Stamp, Hairline, Field + the btn() style fn).

import type { CSSProperties, ReactNode } from "react";
import { C, F, DEPT_MAP } from "../theme";

export function Eyebrow({ children, c = C.brassDk, style }: { children: ReactNode; c?: string; style?: CSSProperties }) {
  return <div style={{ fontFamily: F.body, fontSize: 10.5, fontWeight: 800, color: c, letterSpacing: 1.4, textTransform: "uppercase", ...style }}>{children}</div>;
}

export function Hairline({ c = C.hairlineLt, style }: { c?: string; style?: CSSProperties }) {
  return <div style={{ height: 1, background: `linear-gradient(90deg,transparent,${c} 6%,${c} 94%,transparent)`, ...style }} />;
}

export function Card({ children, style, pad = 16, edge = false, onClick }: { children: ReactNode; style?: CSSProperties; pad?: number; edge?: boolean; onClick?: () => void }) {
  return (
    <div onClick={onClick} style={{ background: C.card, borderRadius: 16, border: `1px solid ${C.hairlineLt}`, boxShadow: C.shadowS, padding: pad, position: "relative", overflow: "hidden", cursor: onClick ? "pointer" : "default", ...style }}>
      {edge && <div style={{ position: "absolute", top: 0, left: 16, right: 16, height: 2, background: `linear-gradient(90deg,transparent,${C.brassLt} 20%,${C.brassLt} 80%,transparent)`, opacity: 0.5 }} />}
      {children}
    </div>
  );
}

export interface Tone { fg: string; bg: string; bd: string; }
export function Pill({ children, tone, small, style }: { children: ReactNode; tone?: Tone; small?: boolean; style?: CSSProperties }) {
  const fg = tone?.fg || C.brassDk, bg = tone?.bg || C.brassBg, bd = tone?.bd || "rgba(167,117,28,0.2)";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: small ? "2px 8px" : "4px 10px", borderRadius: 999, background: bg, color: fg, fontFamily: F.body, fontSize: small ? 10.5 : 11.5, fontWeight: 800, letterSpacing: 0.3, border: `1px solid ${bd}`, whiteSpace: "nowrap", ...style }}>{children}</span>
  );
}

export function Stamp({ children, c = C.brassDk, bg = C.brassBg, bd, style }: { children: ReactNode; c?: string; bg?: string; bd?: string; style?: CSSProperties }) {
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 7px", fontFamily: F.mono, fontSize: 10.5, fontWeight: 600, color: c, background: bg, border: `1px dashed ${bd || "rgba(167,117,28,0.3)"}`, borderRadius: 4, letterSpacing: 0.5, whiteSpace: "nowrap", ...style }}>{children}</span>;
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return <div><Eyebrow style={{ marginBottom: 6 }}>{label}</Eyebrow>{children}</div>;
}

export type BtnVariant = "primary" | "secondary" | "soft" | "ghost";
export function btn(variant: BtnVariant = "primary"): CSSProperties {
  const base: CSSProperties = { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, fontFamily: F.body, fontWeight: 800, fontSize: 13.5, padding: "9px 14px", borderRadius: 11, minHeight: 40, cursor: "pointer", border: "none", letterSpacing: 0.2, whiteSpace: "nowrap" };
  if (variant === "primary") return { ...base, background: C.brassDk, color: C.brassGlow, boxShadow: "inset 0 1px 0 rgba(244,226,168,0.3), 0 2px 6px rgba(122,84,11,0.16)" };
  if (variant === "secondary") return { ...base, background: C.card, color: C.brassDk, border: `1px solid ${C.hairline}`, boxShadow: C.shadowS };
  if (variant === "soft") return { ...base, background: C.brassBg, color: C.brassDk, border: "1px solid rgba(167,117,28,0.18)" };
  return { ...base, background: "transparent", color: C.inkSoft };
}

// shared text input style (matches bp-line.jsx `inp`)
export const inp: CSSProperties = { width: "100%", boxSizing: "border-box", border: `1px solid ${C.hairline}`, borderRadius: 8, padding: "7px 9px", fontFamily: F.body, fontSize: 12.5, fontWeight: 700, color: C.ink, background: C.card, outline: "none" };

// Department chip (bp-ui.jsx Dept) — coloured per the DEPT_MAP taxonomy.
export function Dept({ id, small, dot = true, style }: { id: string; small?: boolean; dot?: boolean; style?: CSSProperties }) {
  const d = DEPT_MAP[id];
  if (!d) return null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: small ? "2px 8px" : "3px 10px", borderRadius: 999, background: d.bg, color: d.fg, border: `1px solid ${d.bd}`, fontFamily: F.body, fontSize: small ? 10 : 11, fontWeight: 800, letterSpacing: 0.3, whiteSpace: "nowrap", ...style }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: 3, background: d.fg, flexShrink: 0 }} />}
      {d.short}
    </span>
  );
}

// Initials avatar (bp-ui.jsx Avatar).
export function Avatar({ name = "", size = 32, fg = C.brassDk, bg = C.brassBg, style }: { name?: string; size?: number; fg?: string; bg?: string; style?: CSSProperties }) {
  const init = name.split(" ").map((w) => w[0] ?? "").join("").slice(0, 2).toUpperCase();
  return (
    <div style={{ width: size, height: size, borderRadius: size / 3, background: bg, color: fg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: F.serif, fontWeight: 700, fontSize: size * 0.4, flexShrink: 0, border: `1px solid ${C.hairline}`, ...style }}>{init}</div>
  );
}

// Segmented toggle (bp-ui.jsx Segmented).
export interface SegOption { id: string; label: string; icon?: ReactNode; }
export function Segmented({ options, value, onChange, style }: { options: SegOption[]; value: string; onChange: (id: string) => void; style?: CSSProperties }) {
  return (
    <div style={{ display: "inline-flex", background: C.paperWarm, border: `1px solid ${C.hairline}`, borderRadius: 12, padding: 3, gap: 2, ...style }}>
      {options.map((o) => {
        const on = o.id === value;
        return (
          <button key={o.id} type="button" onClick={() => onChange(o.id)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 13px", borderRadius: 9, border: "none", cursor: "pointer", fontFamily: F.body, fontWeight: 800, fontSize: 13, letterSpacing: 0.2, background: on ? C.card : "transparent", color: on ? C.brassDk : C.inkMute, boxShadow: on ? C.shadowS : "none" }}>
            {o.icon}{o.label}
          </button>
        );
      })}
    </div>
  );
}
